<template>
  <div class="mc-console">
    <!-- Header -->
    <div class="mc-header">
      <h1 class="mc-header__title">Marketing Console</h1>
      <p class="mc-header__subtitle">
        Pick a team to view and manage its marketing — watched pages, screenshot replies, dashboard, and sign-up links.
      </p>
    </div>

    <!-- Tenant picker (native select — lists all teams) -->
    <div class="mc-picker-card">
      <label class="mc-label" for="mc-team-select">Team</label>
      <select
        id="mc-team-select"
        v-model="selectedTenantId"
        class="mc-select"
        :disabled="loading"
      >
        <option value="" disabled>{{ loading ? 'Loading teams…' : 'Select a team…' }}</option>
        <option v-for="t in tenants" :key="t.tenant_id" :value="t.tenant_id">
          {{ optionLabel(t) }}
        </option>
      </select>

      <p v-if="loadError" class="mc-error">
        Couldn't load teams: {{ loadError }}
        <button type="button" class="mc-retry" @click="loadTenants">Retry</button>
      </p>
      <p v-else-if="!loading" class="mc-hint">{{ tenants.length }} team{{ tenants.length === 1 ? '' : 's' }}</p>
    </div>

    <!-- Embedded marketing page for the selected tenant -->
    <template v-if="selectedTenantId">
      <button type="button" class="mc-back" @click="clearTenant">
        ← Back to all pages
      </button>
      <MarketingPage
        :key="selectedTenantId"
        :tenant-id="selectedTenantId"
        :super-admin="true"
      />
    </template>

    <!-- Grouped overview of every tenant's watched pages -->
    <div v-else class="mc-links-card">
      <div class="mc-links-card__head">
        <h2 class="mc-links-card__title">All watched pages</h2>
        <p class="mc-links-card__subtitle">Every team's Pages to watch in one place. Use “Manage” to open a team.</p>
      </div>

      <!-- Loading -->
      <div v-if="loadingLinks" class="mc-links-state">Loading pages…</div>

      <!-- Error -->
      <div v-else-if="linksError" class="mc-links-state mc-links-state--error">
        Couldn't load pages: {{ linksError }}
        <button type="button" class="mc-retry" @click="loadPages">Retry</button>
      </div>

      <!-- Empty -->
      <div v-else-if="!groups.length" class="mc-links-state">No watched pages yet.</div>

      <!-- Grouped table -->
      <table v-else class="mc-table">
        <thead>
          <tr>
            <th class="mc-th mc-th--agent">Label</th>
            <th class="mc-th">URL</th>
            <th class="mc-th">Platform</th>
            <th class="mc-th mc-th--created">Created</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groups">
            <!-- Tenant header row -->
            <tr :key="`group-${group.tenant_id}`" class="mc-grouprow">
              <td class="mc-grouprow__cell" colspan="4">
                <span class="mc-grouprow__name">{{ group.tenant_name }}</span>
                <span class="mc-grouprow__count">{{ group.page_count }} page{{ group.page_count === 1 ? '' : 's' }}</span>
                <button type="button" class="mc-manage" @click="selectTenant(group.tenant_id)">Manage</button>
              </td>
            </tr>
            <!-- Page rows -->
            <tr
              v-for="page in group.pages"
              :key="`page-${page.id}`"
              class="mc-linkrow"
            >
              <td class="mc-td mc-td--agent">{{ page.label || '—' }}</td>
              <td class="mc-td">
                <div class="mc-linkcell">
                  <a :href="page.url" target="_blank" rel="noopener noreferrer" class="mc-url">{{ page.url }}</a>
                  <button type="button" class="mc-copy" title="Copy URL" @click="copyText(page.url)">Copy</button>
                </div>
              </td>
              <td class="mc-td">{{ formatPlatform(page.platform) }}</td>
              <td class="mc-td mc-td--created">{{ formatDate(page.created_at) }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import MarketingPage from './index.vue';
import { listAllWatchedPages, humanizeError } from './marketing_api';

export default {
  name: 'MarketingConsole',
  components: { MarketingPage },
  data() {
    return {
      selectedTenantId: '',
      tenants: [],
      loading: false,
      loadError: '',
      groups: [],
      loadingLinks: false,
      linksError: '',
    };
  },
  mounted() {
    this.loadTenants();
    this.loadPages();
  },
  methods: {
    ...mapActions('Deployment', ['searchTenants']),
    // Empty query => the endpoint returns every tenant, sorted by name.
    async loadTenants() {
      this.loading = true;
      this.loadError = '';
      try {
        const rows = await this.searchTenants('');
        this.tenants = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error('[marketing-console] failed to load tenants', e);
        this.loadError = (e && e.message) || 'request failed';
        this.tenants = [];
      } finally {
        this.loading = false;
      }
    },
    // Grouped overview of every tenant's watched pages.
    async loadPages() {
      this.loadingLinks = true;
      this.linksError = '';
      try {
        const groups = await listAllWatchedPages();
        this.groups = Array.isArray(groups) ? groups : [];
      } catch (e) {
        console.error('[marketing-console] failed to load watched pages', e);
        this.linksError = humanizeError(e);
        this.groups = [];
      } finally {
        this.loadingLinks = false;
      }
    },
    selectTenant(id) {
      this.selectedTenantId = id;
    },
    clearTenant() {
      this.selectedTenantId = '';
      // Re-fetch so pages added/edited/archived inside the team manager
      // are reflected in the overview.
      this.loadPages();
    },
    optionLabel(t) {
      const hint = t.email || t.phone;
      return hint ? `${t.name} · ${hint}` : t.name;
    },
    async copyText(text) {
      if (!text) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        }
      } catch (e) {
        console.error('[marketing-console] copy failed', e);
      }
    },
    formatPlatform(value) {
      if (!value) return '—';
      const s = String(value);
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    formatDate(value) {
      if (!value) return '—';
      // created_at may arrive as an ISO string, an epoch (ms/seconds), or a
      // Firestore-style { _seconds } / { seconds } timestamp.
      let date;
      if (typeof value === 'object') {
        const secs = value._seconds != null ? value._seconds : value.seconds;
        date = secs != null ? new Date(secs * 1000) : null;
      } else if (typeof value === 'number') {
        date = new Date(value < 1e12 ? value * 1000 : value);
      } else {
        date = new Date(value);
      }
      if (!date || Number.isNaN(date.getTime())) return '—';
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

.mc-console {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 20px;
  max-width: 1320px;
  margin: 0 auto;
}

.mc-header {
  margin-bottom: 14px;
}

.mc-header__title {
  font-size: 22px;
  font-weight: 700;
  color: #0C2454;
  margin: 0;
  line-height: 28px;
}

.mc-header__subtitle {
  font-size: 13px;
  color: #5E6B85;
  margin: 2px 0 0;
  max-width: 720px;
}

.mc-picker-card {
  background: #FFFFFF;
  border: 1px solid #E6ECF5;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  max-width: 560px;
}

.mc-label {
  display: block;
  font-size: 11px !important;
  font-weight: 600 !important;
  color: #344054 !important;
  margin: 0 0 6px;
  line-height: 1.3 !important;
  /* Neutralize global app.scss `label { transform: translateY(-18px);
     font-size: 15px; ... !important }` (Vuetify floating-label rule). */
  transform: none !important;
  max-width: 100% !important;
}

.mc-select {
  width: 100%;
  height: 40px;
  border: 1.5px solid #D9E1F0;
  border-radius: 8px;
  padding: 0 34px 0 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #14213D;
  background-color: #FFFFFF;
  outline: none;
  cursor: pointer;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2398A2B3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
}

.mc-select:focus {
  border-color: #2F6FED;
  box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.08);
}

.mc-select:disabled {
  background-color: #F4F6FA;
  cursor: default;
}

.mc-hint {
  font-size: 12px;
  color: #98A2B3;
  margin: 8px 0 0;
}

.mc-error {
  font-size: 12px;
  color: #C62828;
  margin: 8px 0 0;
}

.mc-retry {
  margin-left: 8px;
  border: 1px solid #D6E4FF;
  background: #FFFFFF;
  color: #2F6FED;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}

.mc-retry:hover {
  background: #EEF4FF;
}

.mc-empty {
  border: 1px dashed #C7D4E6;
  border-radius: 12px;
  background: #FAFBFE;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #98A2B3;
  font-size: 14px;
  text-align: center;
  padding: 24px;
}

/* Back-to-all-links button (shown above the embedded manager) */
.mc-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  border: 1px solid #D6E4FF;
  background: #FFFFFF;
  color: #2F6FED;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}

.mc-back:hover {
  background: #EEF4FF;
}

/* Grouped "all source links" overview */
.mc-links-card {
  background: #FFFFFF;
  border: 1px solid #E6ECF5;
  border-radius: 12px;
  padding: 16px;
  overflow: hidden;
}

.mc-links-card__head {
  margin-bottom: 12px;
}

.mc-links-card__title {
  font-size: 16px;
  font-weight: 700;
  color: #0C2454;
  margin: 0;
  line-height: 22px;
}

.mc-links-card__subtitle {
  font-size: 12px;
  color: #5E6B85;
  margin: 2px 0 0;
}

.mc-links-state {
  padding: 28px 12px;
  text-align: center;
  color: #98A2B3;
  font-size: 14px;
}

.mc-links-state--error {
  color: #C62828;
}

.mc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.mc-th {
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #98A2B3;
  padding: 8px 12px;
  border-bottom: 1px solid #E6ECF5;
}

.mc-th--agent {
  width: 18%;
}

.mc-th--created {
  width: 14%;
  white-space: nowrap;
}

/* Tenant header row — visually distinct from link rows */
.mc-grouprow__cell {
  background: #F4F7FC;
  border-top: 1px solid #E6ECF5;
  border-bottom: 1px solid #E6ECF5;
  padding: 10px 12px;
}

.mc-grouprow__name {
  font-size: 14px;
  font-weight: 700;
  color: #0C2454;
}

.mc-grouprow__count {
  margin-left: 10px;
  font-size: 12px;
  color: #98A2B3;
}

.mc-manage {
  float: right;
  border: 1px solid #2F6FED;
  background: #2F6FED;
  color: #FFFFFF;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}

.mc-manage:hover {
  background: #2560D6;
  border-color: #2560D6;
}

.mc-linkrow:hover {
  background: #FAFBFE;
}

.mc-td {
  padding: 10px 12px;
  border-bottom: 1px solid #EEF2F8;
  color: #14213D;
  vertical-align: top;
}

.mc-td--agent {
  font-weight: 500;
}

.mc-td--created {
  white-space: nowrap;
  color: #5E6B85;
}

.mc-source {
  color: #14213D;
}

.mc-campaign {
  font-size: 12px;
  color: #98A2B3;
  margin-top: 2px;
}

.mc-linkcell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mc-url {
  color: #2F6FED;
  text-decoration: none;
  word-break: break-all;
}

.mc-url:hover {
  text-decoration: underline;
}

.mc-copy {
  flex: none;
  border: 1px solid #D6E4FF;
  background: #FFFFFF;
  color: #2F6FED;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}

.mc-copy:hover {
  background: #EEF4FF;
}
</style>
